import { useState, useEffect, useReducer } from 'react';

import { Learnosity } from './types';
import { StatusTypes } from '../../lib/loadingStatusTypes';

import { useAulaEditorContext } from '../../EditorContext';
import { createTypedReducer } from '../../lib/fetchReducer';

export const useLearnositySdk = () => {
  const [learnositySDKLoaded, setLearnositySDKLoaded] = useState(false);

  useEffect(() => {
    const { LearnosityItems } = window as any;
    if (LearnosityItems) {
      setLearnositySDKLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://items.learnosity.com/?v2022.3.LTS';
    document.body.appendChild(script);
    script.onload = () => {
      setLearnositySDKLoaded(true);
    };
  }, []);

  return learnositySDKLoaded;
};

/**
 *
 * useFetchInlineItem fetches the payload to be able to init learnosity.
 *
 */
export const useFetchInlineItem = (
  referenceId: string,
  learnositySDKLoaded: boolean
) => {
  const { config } = useAulaEditorContext();
  const { accessToken, sessionToken, spaceId, apiUrl, materialId } = config;
  const typedReducer = createTypedReducer<Learnosity.ApiResponse.InlineItem>();
  const [state, dispatch] = useReducer(typedReducer, {
    status: StatusTypes.IDLE,
    data: {} as Learnosity.ApiResponse.InlineItem,
  });

  const [retryFlag, setRetryFlag] = useState(false);

  const retry = () => {
    setRetryFlag(!retryFlag);
  };

  useEffect(() => {
    const getDataReq = async ({ signal: abortSignal }: AbortController) => {
      if (!learnositySDKLoaded) {
        return;
      }

      if (state.status === StatusTypes.LOADING) {
        return;
      }

      try {
        dispatch({ type: StatusTypes.LOADING });

        const request = await fetch(`${apiUrl}/learnosity/inline-item`, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
            'x-session-token': sessionToken,
          },
          signal: abortSignal,
          body: JSON.stringify({
            materialId,
            space: spaceId,
            itemRef: referenceId,
          }),
        });

        const result = await request.json();

        if (result?.errors?.length > 0) {
          dispatch({ type: StatusTypes.FAILED, payload: result.errors[0] });
          return;
        }

        dispatch({ type: StatusTypes.SUCCESS, payload: result });
      } catch (err) {
        dispatch({ type: StatusTypes.FAILED, payload: err });
      }
    };

    const abortController = new AbortController();
    void getDataReq(abortController);

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessToken,
    sessionToken,
    config.apiUrl,
    spaceId,
    retryFlag,
    learnositySDKLoaded,
  ]);

  return { state, retry };
};

/**
 * Fetches all items from the item bank recursively
 */
const fetchAllItems: any = async ({
  accessToken,
  sessionToken,
  url,
  spaceId,
  abortSignal,
  next,
  data = [],
}: {
  accessToken: string;
  sessionToken: string;
  url: string;
  spaceId: string;
  abortSignal: AbortSignal;
  next?: string | boolean;
  data?: Learnosity.ApiResponse.Item[];
}) => {
  const request = await fetch(url, {
    method: 'post',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'x-session-token': sessionToken,
    },
    signal: abortSignal,
    body: JSON.stringify({
      spaceId,
      limit: 50,
      next,
    }),
  });

  const result = await request.json();

  // When there are no items for a space, Learnosity
  // returns a 400 request with the error
  // "Error code 20014 - Reason: Tags not found: Space:<spaceId"
  // Ideally this should be handled in the backend, but for now
  // we leave this here to indicate to the user that there are no
  // items since a 400 request is unlikely for other reasons.
  if (request.status === 400) {
    return [];
  }

  if (result?.errors?.length > 0) {
    return result;
  }

  if (result.meta.next) {
    return fetchAllItems({
      accessToken,
      sessionToken,
      url,
      spaceId,
      abortSignal,
      next: result.meta.next,
      data: result.data,
    });
  }

  return [...result.data, ...data];
};

const ENDPOINT_URL = 'learnosity/getItems';

export const useFetchItems = () => {
  const { config } = useAulaEditorContext();
  const { accessToken, sessionToken, spaceId } = config;
  const typedReducer = createTypedReducer<Learnosity.ApiResponse.Item[]>();
  const [state, dispatch] = useReducer(typedReducer, {
    status: StatusTypes.IDLE,
    data: [],
  });
  const [retryFlag, setRetryFlag] = useState(false);

  const retry = () => {
    setRetryFlag(!retryFlag);
  };

  useEffect(() => {
    const getDataReq = async ({ signal: abortSignal }: AbortController) => {
      if (state.status === StatusTypes.LOADING) {
        return;
      }

      try {
        dispatch({ type: StatusTypes.LOADING });

        const result = await fetchAllItems({
          sessionToken,
          accessToken,
          url: `${config.apiUrl}/${ENDPOINT_URL}`,
          spaceId,
          abortSignal,
        });

        if (result?.errors?.length > 0) {
          dispatch({ type: StatusTypes.FAILED, payload: result.errors[0] });
          return;
        }

        dispatch({ type: StatusTypes.SUCCESS, payload: result });
      } catch (err) {
        dispatch({ type: StatusTypes.FAILED, payload: err });
      }
    };

    const abortController = new AbortController();
    void getDataReq(abortController);

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, sessionToken, config.apiUrl, spaceId, retryFlag]);

  return { state, retry };
};
